import React, { useEffect, useMemo } from 'react'
import { Modal, Form, Button, Space, Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../../types/company/Company'
import { CustomReportArgument } from '../../../../../redux/context/customReports/types'
import {
  CompanyDimensionPicker,
  CompanySelection
} from '../../../../../shared/components/custom/CompanyDimensionPicker'
import {
  mergeArgumentsToPickerValue,
  splitPickerToCompanyArgValues,
  splitPickerToDimensionArgValues
} from './argumentUtils'

interface ArgumentModalProps {
  visible: boolean
  loading: boolean
  arguments: CustomReportArgument[]
  defaultArguments: CustomReportArgument[]
  onCancel: () => void
  onSave: (values: CustomReportArgument[]) => void
  onUse: (values: CustomReportArgument[]) => void
  companies: Company[]
}

interface ArgumentFormValues {
  values: CompanySelection[]
}

const ArgumentModal: React.FC<ArgumentModalProps> = ({
  visible,
  loading,
  companies,
  arguments: args,
  defaultArguments,
  onCancel,
  onSave,
  onUse
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<ArgumentFormValues>()

  const companyArg = useMemo(() => args.find(a => a.type === 'company'), [args])
  const dimensionArg = useMemo(() => args.find(a => a.type === 'dimension'), [args])

  const companyOptions = useMemo(() => companies.map(c => ({ id: c.id, name: c.name })), [companies])

  const pickerValue = useMemo(
    () => mergeArgumentsToPickerValue(companyArg, dimensionArg, companies),
    [companyArg, dimensionArg, companies]
  )

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({ values: pickerValue })
    }
  }, [visible, pickerValue, form])

  const buildUpdatedArguments = (selections: CompanySelection[]): CustomReportArgument[] => {
    const updated: CustomReportArgument[] = []

    if (companyArg) {
      updated.push({
        ...companyArg,
        values: splitPickerToCompanyArgValues(selections)
      })
    }

    if (dimensionArg) {
      updated.push({
        ...dimensionArg,
        values: splitPickerToDimensionArgValues(selections)
      })
    }

    return updated
  }

  const handleFinish = (formValues: ArgumentFormValues) => {
    const updatedArgs = buildUpdatedArguments(formValues.values)
    onSave(updatedArgs)
  }

  const handleUse = () => {
    form
      .validateFields()
      .then(formValues => {
        const updatedArgs = buildUpdatedArguments(formValues.values)
        onUse(updatedArgs)
      })
      .catch(() => undefined)
  }

  const handleResetToDefaults = () => {
    const defaultCompanyArg = defaultArguments.find(a => a.type === 'company')
    const defaultDimensionArg = defaultArguments.find(a => a.type === 'dimension')
    const defaultPickerValue = mergeArgumentsToPickerValue(defaultCompanyArg, defaultDimensionArg, companies)
    form.setFieldsValue({ values: defaultPickerValue })
  }

  return (
    <Modal
      data-cy="arguments-modal"
      title={t('global:arguments')}
      open={visible}
      width={800}
      maskClosable={false}
      onCancel={onCancel}
      destroyOnHidden
      footer={null}
    >
      {args.length > 0 && (
        <Form form={form} layout="vertical" initialValues={{ values: pickerValue }} onFinish={handleFinish}>
          <Form.Item name="values" rules={[{ required: true, message: t('global:required-field') }]}>
            <CompanyDimensionPicker companies={companyOptions} />
          </Form.Item>

          <Form.Item>
            <Flex justify="space-between">
              <Space>
                <Button data-cy="arguments-reset" onClick={handleResetToDefaults}>
                  {t('actions:resetDefaults')}
                </Button>
              </Space>
              <Space>
                <Button data-cy="arguments-cancel" onClick={onCancel}>
                  {t('actions:cancel')}
                </Button>
                <Button data-cy="arguments-use" onClick={handleUse}>
                  {t('actions:use')}
                </Button>
                <Button data-cy="arguments-save" loading={loading} type="primary" htmlType="submit">
                  {t('actions:saveDefaults')}
                </Button>
              </Space>
            </Flex>
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}

export default ArgumentModal
